/**
 * VG 2.0 — presentation formatting helpers (Phase 3B.2).
 *
 * Deterministic Italian localization + number formatting for the VALUTA
 * result UI. Kept OUTSIDE the frozen Phase 2A numerical core. This module
 * MUST NOT alter valuation values — presentation only.
 */

// §geo-honest · L'utente a volte scrive il CAP nel campo Comune
// ("00128, Roma" o "Roma 00128"). Separiamo un CAP italiano (5 cifre) dal
// nome del comune, senza toccare i valori salvati altrove.
export function splitMunicipalityCap(raw) {
  const s = String(raw || "").trim();
  const capMatch = s.match(/\b\d{5}\b/);
  const cap = capMatch ? capMatch[0] : null;
  const comune = s
    .replace(/\b\d{5}\b/, "")
    .replace(/[,;]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return { comune, cap };
}


// ---------- Number formatting ---------------------------------------------
// One shared it-IT formatter; always groups thousands with "." separator.
const _INT_FMT = new Intl.NumberFormat("it-IT", {
  useGrouping: "always",
  maximumFractionDigits: 0,
});

export function fmtInt(n) {
  if (n === null || n === undefined || !Number.isFinite(Number(n))) return null;
  return _INT_FMT.format(Number(n));
}

export function fmtEur(n) {
  const s = fmtInt(n);
  return s === null ? null : `€ ${s}`;
}

// ---------- Precision action localization ---------------------------------
// Deterministic mapping from Phase 2A backend action strings (or codes) to
// concise, actionable Italian copy. Unmapped items are hidden (§2 fallback).

const _ACTION_MAP = {
  // Feature-completion actions coming from Phase 2A engine (verbatim strings).
  "Provide surface_sqm to calculate the absolute property value.":
    "Aggiungi la superficie per ottenere anche il valore complessivo.",
  "Provide condition, energy class, floor, elevator, terrace, garden, parking.":
    "Aggiungi condizioni, classe energetica, piano e dotazioni per una stima più precisa.",
  // NO_NUMERICAL_ANCHOR technical actions — surface as user-actionable copy
  // when meaningful, otherwise hide.
  "provide coordinates or municipality for local OMI/Market Stats lookup":
    "Verifica l’indirizzo dell’immobile per collegarlo alle fonti locali.",
  "connect asking_market provider": null,        // internal, hide
  "connect declared_transactions provider": null, // internal, hide
};

// Localized Italian labels for the P0 geo-chain states surfaced on
// ``analysis.geo_state``. Rendered as banner/CTA on ValutaResult.jsx.
export const GEO_STATE_COPY_IT = {
  needs_housenumber: {
    title: "Serve il numero civico",
    body: "Abbiamo riconosciuto la strada. Per collegare l’immobile alla zona di mercato corretta serve anche il numero civico.",
    cta: "Aggiungi il numero civico",
  },
  ambiguous: {
    title: "Verifica l’indirizzo",
    body: "Abbiamo trovato più indirizzi compatibili con quello che hai inserito. Conferma quello corretto per allineare la stima ai dati della zona.",
    cta: "Verifica l’indirizzo",
  },
  not_found: {
    title: "Serve l’indirizzo completo",
    body: "Per collegare l’immobile alla zona di mercato corretta ci serve l’indirizzo nel formato: via (e numero civico) e comune — ad esempio «Via Vincenzo Monti 4, Roma». Controlla il nome della via e il comune e aggiorna l’indirizzo qui sotto.",
    cta: "Correggi l’indirizzo",
  },
  municipality_fallback: {
    title: "Stima basata sul comune",
    body: "Non siamo riusciti a collegare questo indirizzo a un punto preciso, quindi la stima qui sotto è calcolata sul comune indicato, non sul tuo indirizzo esatto: leggila come un ordine di grandezza, con una fascia volutamente ampia. Per una stima mirata aggiungi l’indirizzo completo: via (e numero civico) e comune.",
    cta: "Correggi l’indirizzo",
  },
  unavailable: {
    title: "Servizio temporaneamente non disponibile",
    body: "In questo momento non riusciamo a collegare l’indirizzo ai dati territoriali. I dati che hai inserito sono stati salvati: puoi riprovare senza ricominciare il percorso.",
    cta: "Riprova il collegamento",
  },
  street_multi_zone: {
    title: "Stima a livello di via",
    body: "Abbiamo riconosciuto la via, che però attraversa più zone di mercato. La stima qui sotto usa il livello più ampio ancora affidabile per questa via. Il numero civico è facoltativo: se vuoi, aggiungilo per una stima più mirata.",
    cta: "Aggiungi il numero civico (facoltativo)",
  },
};

// Comune names vary a lot by neighbourhood (notably Roma); when the
// estimate falls back to the comune centroid we say so explicitly and
// substitute the comune name into the body.
function _withMunicipality(copy, municipality) {
  if (!municipality) return copy;
  return {
    ...copy,
    body: copy.body.replace(
      "sul comune indicato",
      `sul comune di ${municipality}`,
    ),
  };
}

/**
 * Interpret a ``geo_state`` block returned by the valuation API and
 * return the localized banner descriptor, or null when no banner is
 * needed (fully resolved, precision building/street with civico).
 */
export function localizeGeoState(geo_state) {
  if (!geo_state || typeof geo_state !== "object") return null;
  if (geo_state.status === "resolved" && geo_state.housenumber_required) {
    return { code: "needs_housenumber", ...GEO_STATE_COPY_IT.needs_housenumber };
  }
  if (geo_state.status === "ambiguous") {
    return { code: "ambiguous", ...GEO_STATE_COPY_IT.ambiguous };
  }
  if (geo_state.status === "not_found") {
    return { code: "not_found", ...GEO_STATE_COPY_IT.not_found };
  }
  if (geo_state.status === "municipality_fallback") {
    return {
      code: "municipality_fallback",
      municipality: geo_state.fallback_municipality || null,
      ..._withMunicipality(
        GEO_STATE_COPY_IT.municipality_fallback,
        geo_state.fallback_municipality,
      ),
    };
  }
  if (geo_state.status === "unavailable") {
    return { code: "unavailable", ...GEO_STATE_COPY_IT.unavailable };
  }
  // §Paolucci · via che attraversa più zone OMI senza civico: nota NON
  // bloccante (il civico resta facoltativo), copertura dichiarata.
  if (geo_state.status === "resolved"
      && geo_state.housenumber_optional_improvement
      && geo_state.street_spans_multiple_zones) {
    return { code: "street_multi_zone", ...GEO_STATE_COPY_IT.street_multi_zone };
  }
  return null;
}

// Per-feature-code mapping (for future object-shaped actions {code, label}).
const _CODE_MAP = {
  surface_sqm:  "Aggiungi la superficie per ottenere anche il valore complessivo.",
  condition:    "Indica le condizioni dell’immobile.",
  energy_class: "Indica la classe energetica.",
  floor:        "Indica il piano.",
  elevator:     "Indica se è presente l’ascensore.",
  terrace:      "Indica se è presente un terrazzo.",
  garden:       "Indica se è presente un giardino.",
  parking:      "Indica se è presente un posto auto o box.",
  bathrooms:    "Indica il numero di bagni.",
  location:     "Verifica l’indirizzo dell’immobile per collegarlo alle fonti locali.",
};

/**
 * Localize one precision action (string OR {code, label}).
 * Returns Italian copy, or null to hide (unmapped/internal).
 * Never leaks backend English.
 */
export function localizePrecisionAction(action) {
  if (action === null || action === undefined) return null;

  if (typeof action === "string") {
    const key = action.trim();
    if (Object.prototype.hasOwnProperty.call(_ACTION_MAP, key)) {
      return _ACTION_MAP[key]; // may be null (hide)
    }
    // Unknown raw backend string → hide rather than leak English.
    return null;
  }

  if (typeof action === "object") {
    const code = (action.code || "").toString().trim();
    if (code && Object.prototype.hasOwnProperty.call(_CODE_MAP, code)) {
      return _CODE_MAP[code];
    }
    // Best-effort: try to match action.label against known English strings.
    if (typeof action.label === "string") {
      return localizePrecisionAction(action.label);
    }
    return null;
  }
  return null;
}

/**
 * Localize a list of precision actions, filter out hidden items,
 * enforce max 3 shown (§2 last clause).
 *
 * ``context.hasMunicipality`` suppresses the "Indica il comune…" hint
 * when the municipality is already on the Case — the message would be
 * contradictory ("Comune riconosciuto: Roma" + "Indica il comune").
 */
export function localizePrecisionActions(list, { max = 3, context = {} } = {}) {
  if (!Array.isArray(list)) return [];
  const out = [];
  const suppressLocation = !!context.hasMunicipality || !!context.hasStreet;
  const suppressed = new Set([
    "Verifica l’indirizzo dell’immobile per collegarlo alle fonti locali.",
    "Indica il comune o l’indirizzo per abilitare le fonti locali.",
  ]);
  for (const a of list) {
    const t = localizePrecisionAction(a);
    if (!t) continue;
    if (suppressLocation && suppressed.has(t)) continue;
    out.push(t);
    if (out.length >= max) break;
  }
  return out;
}
